/**
 * @description App component
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module App
 */

import React from "react";
import PropTypes from "prop-types";

import SimpleBar from "simplebar-react";

import "./styles/App.css";
import "simplebar/dist/simplebar.min.css";

import HeatmapService from "./services/HeatmapService";
import * as HeatmapHelpers from "./helpers/HeatmapHelpers";
import NotificationService from "./services/NotificationService";

import ResetContext from "./context/ResetContext";

import Notifications from "./components/Notifications";
import Nav from "./components/Nav";
import Menu from "./components/Menu";
import Footer from "./components/Footer";
import Route from "./components/Route";
import HeatmapDragService from './services/HeatmapDragService';
import JunctionTooltip from './components/JunctionTooltip';
import LinkTooltip from './components/LinkTooltip';
import CONSTANTS from "./config/Constants";

/**
 * @description The App component.
 * @class
 * @extends {React.Component}
 */
class App extends React.Component {
    
    /**
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        this.state = {
            notifications: [],
            selectedCarriageways: [],
            routes: [],
            delays: [],
            junctions: [],
            links: [],
            routesPeriods: [],
            showMenu: false,
            selectedDate: HeatmapHelpers.getDateStringFromDate(new Date()),
            showLiveData: true,
            zoomLevel: 1,
            selectedDelays: [true, true, true, true, true, true, true],
            showLinkTooltip: false,
            linkTooltipData: {},
            showJunctionTooltip: false,
            junctionTooltipData: {}
        };
        this.notificationService = new NotificationService(
            this.handleNotificationsUpdate.bind(this)
        );
        this.resetMethods = {};
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        if (!this.props.browserSupported) {
            const unsupportedBrowserMessage = [
                <div key="0">
                    <span>
                        The browser version that you are using is currently
                        unsupported and may result in a degraded experience.
                        <br />
                        This app is currently supported in the latest versions of Google Chrome, Mozilla Firefox and
                        Microsoft Edge.
                    </span>
                </div>
            ];

            this.addNotification({
                title: "Unsupported Browser",
                message: unsupportedBrowserMessage,
                action: () => null, // onclose handler is called too
                onClose: this.loadHeatmapService.bind(this)
            });
        } else {
            this.loadHeatmapService();
            this.heatmapDragService = new HeatmapDragService(this.scrollbarRef);
        }

        this.setEnvironmentIdentifier(process.env.ENVIRONMENT_NAME);
    }

    /**
     * @inheritdoc
     */
    componentDidUpdate(prevProps, prevState) {
        if (prevState.selectedDelays !== this.state.selectedDelays) {
            HeatmapHelpers.setHeatmapColours(
                this.state.selectedDelays,
                this.state.delays
            );
        }
    }

    /**
     * @inheritdoc
     */
    render() {
        this.scrollbarRef = React.createRef();

        return (
            <ResetContext.Provider value={this.manageResetMethods.bind(this)}>
                <div className="App">
                    <Nav
                        environmentName={process.env.ENVIRONMENT_NAME}
                        routeSelections={this.state.selectedCarriageways}
                        selectedDate={this.state.selectedDate}
                        onChangeDate={this.handleSelectedDateChange.bind(this)}
                        onChangeRouteOptions={this.handleSelectedRouteDataChange.bind(
                            this
                        )}
                        onResetFilters={this.handleResetFilters.bind(this)}
                    ></Nav>
                    <Menu
                        showMenu={this.state.showMenu}
                        delays={this.state.delays}
                        selectedDelays={this.state.selectedDelays}
                        onChangeSelectedDelays={this.handleSelectedDelaysChange.bind(
                            this
                        )}
                    ></Menu>
                    <Notifications
                        notifications={this.state.notifications}
                        onCloseNotification={this.closeNotification.bind(this)}
                        onClickNotificationAction={this.handleNotificationActionClick.bind(
                            this
                        )}
                    ></Notifications>
                    <div className="content">
                        {/* SimpleBar used here for app level scrolling/consistent scrollbar styling */}
                        <SimpleBar
                            ref={this.scrollbarRef}
                            autoHide="false"
                            forceVisible="true"
                            className="content-simplebar">
                            {this.state.routes.length > 0 &&
                                this.state.routes.map(route => (
                                    <Route
                                        key={route.name}
                                        route={route}
                                        showLiveData={this.state.showLiveData}
                                        junctions={this.state.junctions}
                                        links={this.state.links}
                                        routesPeriods={this.getRoutesPeriodsForSelectedDate()}
                                        selectedCarriageways={this.state.selectedCarriageways}
                                        openTooltip={this.handleLinkTooltipOpen.bind(this)}
                                        closeTooltip={this.handleLinkTooltipClose.bind(this)}
                                        openJunctionTooltip={this.handleJunctionTooltipOpen.bind(this)}
                                        closeJunctionTooltip={this.handleJunctionTooltipClose.bind(this)}
                                        heatmapDragService={this.heatmapDragService}
                                        scrollbar={this.scrollbarRef}
                                    ></Route>
                                ))}
                            <div className="scroll-space"></div>
                        </SimpleBar>
                    <JunctionTooltip
                        show={this.state.showJunctionTooltip}
                        junction={this.state.junctionTooltipData.junction}
                        event={this.state.junctionTooltipData.event}></JunctionTooltip>
                    <LinkTooltip
                        show={this.state.showLinkTooltip}
                        link={this.state.linkTooltipData.link}
                        event={this.state.linkTooltipData.event}></LinkTooltip>
                    </div>
                    <Footer></Footer>
                </div>
            </ResetContext.Provider>
        );
    }

    /**
     * @description Adds a reset method into the this.resetMethods array.
     * @param {string} id - The id.
     * @param {Function} method - The method to add.
     * @param {boolean} removeMethod - A flag to indicate removing of method.
     */
    manageResetMethods(id, method, removeMethod) {
        this.resetMethods[id] = null;
        
        if (!removeMethod) {
            this.resetMethods[id] = method;
        }        
    }

    /**
     * @description Adds the environment name to the document title in all enviornments except production.
     * @param {string} environmentName - The environment name.
     */
    setEnvironmentIdentifier(environmentName) {
        if (environmentName && environmentName.toLowerCase() === "production") {
            return;
        }

        document.title = `${document.title} (${environmentName})`;
    }

    /**
     * @description Gets all available periods for the selected date.
     * @returns {Object} - An object containing details of carriageways and their periods.
     */
    getRoutesPeriodsForSelectedDate() {
        const selectedDate = this.state.selectedDate
            ? this.state.selectedDate
            : HeatmapHelpers.getDateStringFromDate(new Date());

        return (
            this.state.routesPeriods.find(
                currentDate => currentDate.date === selectedDate
            ) || {}
        );
    }

    /**
     * @description Loads the heatmap service.
     */
    loadHeatmapService() {
        this.heatmapService = new HeatmapService(
            {
                onDataServiceMessage: this.updateLiveData.bind(this)
            },
            this.notificationService
        );

        Promise.all([
            this.heatmapService.getRoutes(),
            this.heatmapService.getDelayData()
        ])
            .then(data => {
                const routeData = HeatmapHelpers.getJunctionsAndLinksFromRoutes(
                        data[0]
                    ),
                    selectedRouteData = HeatmapHelpers.setSelectedRoutes(
                        data[0]
                    );

                this.setState({
                    ...routeData,
                    routes: data[0],
                    selectedCarriageways: HeatmapHelpers.getCarriagewaySelections(
                        selectedRouteData
                    ),
                    delays: data[1]
                });
                HeatmapHelpers.setHeatmapColours(
                    this.state.selectedDelays,
                    data[1]
                );

                this.setPeriodVisibleInterval();

                let carriageways = [];

                this.state.routes.forEach(route => {
                    carriageways = [
                        ...carriageways,
                        ...route.carriageways.map(carriageway => {
                            return {
                                id: carriageway.id,
                                links: carriageway.links.map(link => link.id)
                            };
                        })
                    ];
                });

                HeatmapHelpers.addNewDate(
                    this.state.selectedDate,
                    this.state.routesPeriods,
                    carriageways
                ).then(routesPeriods => {
                    this.setState(
                        {
                            routesPeriods: routesPeriods
                        },
                        () => this.heatmapService.createDataServiceConnection()
                    );
                }).catch(error => {
                    this.addNotification(error);
                });
            })
            .catch(error => {
                this.addNotification(error);
            });
    }

    /**
     * @description Starts the interval from which to update period visibility.
     */
    setPeriodVisibleInterval() {
        const currentTime = new Date(),
            nextPeriod = new Date();

        nextPeriod.setMinutes(
            Math.floor(
                currentTime.getMinutes() / CONSTANTS.UPDATE_INTERVAL_MINS
            ) *
                CONSTANTS.UPDATE_INTERVAL_MINS +
                CONSTANTS.UPDATE_INTERVAL_MINS,
            0,
            0
        );

        // Start the interval at the start of the next time bin.
        setTimeout(() => {
            this.setRecentPeriodVisible();
            // Start the interval
            setInterval(() => {
                this.setRecentPeriodVisible();
            }, CONSTANTS.UPDATE_INTERVAL_MINS * 60000);
        }, nextPeriod - currentTime);
    }

    /**
     * @description Sets the latest timebin for which we have received no data in the alloted time as visible.
     */
    setRecentPeriodVisible() {
        const currentTime = new Date(),
            periodStart = new Date().setMinutes(
                Math.floor(
                    currentTime.getMinutes() / CONSTANTS.UPDATE_INTERVAL_MINS
                ) *
                    CONSTANTS.UPDATE_INTERVAL_MINS -
                    2 * CONSTANTS.UPDATE_INTERVAL_MINS,
                0,
                0
            );

        if (this.checkDateChange()) {
            let carriageways = [];

            this.state.routes.forEach(route => {
                carriageways = [
                    ...carriageways,
                    ...route.carriageways.map(carriageway => {
                        return {
                            id: carriageway.id,
                            links: carriageway.links.map(link => link.id)
                        };
                    })
                ];
            });

            HeatmapHelpers.addNewDatePeriods(
                {
                    date: currentTime,
                    carriageways: carriageways.map(carriageway => {
                        return {
                            id: carriageway.id,
                            periods: []
                        };
                    })
                },
                this.state.routesPeriods,
                carriageways
            ).then(data => {
                const state = {
                    routesPeriods: HeatmapHelpers.setPeriodVisibility(
                        periodStart,
                        data
                    ),
                    selectedDate: HeatmapHelpers.getDateStringFromDate(currentTime)
                };

                this.setState(state);
            });
        } else {
            const routesPeriods = HeatmapHelpers.setPeriodVisibility(
                periodStart,
                this.state.routesPeriods
            );

            this.setState({
                routesPeriods: routesPeriods
            });
        }
    }

    /**
     * @description Processes the 'live' data as received from the API.
     * @param {Object} data - The received data.
     */
    updateLiveData(data) {
        const period = {
            ...data
        };
        let carriageways = [];

        this.state.routes.forEach(route => {
            carriageways = [
                ...carriageways,
                ...route.carriageways.map(carriageway => {
                    return {
                        id: carriageway.id,
                        links: carriageway.links.map(link => link.id)
                    };
                })
            ];
        });

        HeatmapHelpers.addNewDatePeriods(
            period,
            this.state.routesPeriods,
            carriageways
        ).then(data => {
            const state = {
                routesPeriods: data
            };

            if (this.checkDateChange()) {
                state.selectedDate = new Date().toISOString().split("T")[0];
            }

            this.setState(state);
        });

        this.handleLinkTooltipClose();
    }

    /**
     * @description Updates the HeatmapService with new route details.
     * @param {array} [selectedCarriageways=this.state.selectedCarriageways] - The selected carriageways, or all.
     * @param {string} [selectedDate=this.state.selectedDate] - The selected date.
     */
    handleSelectedRouteDataChange(
        selectedCarriageways = this.state.selectedCarriageways,
        selectedDate = this.state.selectedDate
    ) {
        const showLiveData =
            selectedDate === new Date().toISOString().split("T")[0];

        this.setState({
            selectedCarriageways: selectedCarriageways,
            selectedDate: selectedDate,
            showLiveData: showLiveData
        });
        !showLiveData &&
            selectedCarriageways
                .filter(route => route.isSelected >= 0)
                .forEach(route => {
                    this.heatmapService
                        .getHeatmapData(route.name, selectedDate)
                        .then(historicData =>
                            this.processHeatmapData(historicData)
                        )
                        .catch(error => {
                            this.addNotification(error);
                        });
                });
    }

    /**
     * @description Processes the historic heatmap data and sets the state.
     * @param {Array} historicData - An array of historic data.
     */
    processHeatmapData(historicData) {
        let carriageways = [];

        this.state.routes.forEach(route => {
            carriageways = [
                ...carriageways,
                ...route.carriageways.map(carriageway => {
                    return {
                        id: carriageway.id,
                        links: carriageway.links.map(link => link.id)
                    };
                })
            ];
            carriageways = [
                ...carriageways,
                ...route.carriageways.map(carriageway => {
                    return {
                        id: carriageway.id,
                        links: carriageway.links.map(link => link.id)
                    };
                })
            ];
        });

        HeatmapHelpers.addNewDatePeriods(
            historicData,
            this.state.routesPeriods,
            carriageways
        ).then(dateRoutesPeriods => {
            this.setState({
                routesPeriods: dateRoutesPeriods
            });
        });
    }

    /**
     * @description Handles the opening of the junction tooltip.
     * @param {Object} event - The hover event, passed through.
     * @param {Object} junction - The junction for which to display information.
     */
    handleJunctionTooltipOpen(event, junction) {
        this.closeJunctionTooltip && this.closeJunctionTooltip();

        this.setState({
            showJunctionTooltip: true,
            junctionTooltipData: {
                event: event,
                junction: junction
            }
        });
    }

    /**
     * @description Handles the opening of the junction tooltip.
     */
    handleJunctionTooltipClose() {
        this.closeJunctionTooltip && this.closeJunctionTooltip();

        this.setState({
            showJunctionTooltip: false,
            junctionTooltipData: {
                event: null,
                junction: {}
            }
        });
    }

    /**
     * @param {Object} event - The click event, passed through.
     * @param {Object} link - The link for which to display information.
     * @param {Function} setButtonState - A callback for setting the active state of the clicked button.
     */
    handleLinkTooltipOpen(event, link, setButtonState) {
        this.closeLinkTooltip && this.closeLinkTooltip();

        if (!this.state.selectedDelays[link.delayBand.id]) {
            setButtonState(false);

            return;
        }

        this.setState({
            showLinkTooltip: true,
            linkTooltipData: {
                event: event,
                link: link
            }
        });
        this.setToolTipButtonState = setButtonState;
        this.closeLinkTooltip = this.handleLinkTooltipClose.bind(this);
        document.addEventListener('click', this.closeLinkTooltip, {
            once: true
        });
        this.scrollbarRef.current &&
            this.scrollbarRef.current
                .getScrollElement()
                .addEventListener('scroll', this.closeLinkTooltip);
    }

    /**
     * @description Closes the tooltip and sets the button active state to false.
     */
    handleLinkTooltipClose() {
        document.removeEventListener('click', this.closeLinkTooltip);
        this.scrollbarRef.current && this.scrollbarRef.current.getScrollElement().removeEventListener('scroll', this.closeLinkTooltip);
        this.setToolTipButtonState && this.setToolTipButtonState(false);
        this.setState({
            showLinkTooltip: false
        });
        this.setToolTipButtonState = null;
    }

    /**
     * @description Handles the changing of the selected date.
     * @param {string} date - The new value.
     */
    handleSelectedDateChange(date) {
        this.setState({ selectedDate: date });
    }

    /**
     * @description Handles the changing of the selected delays.
     * @param {array} selectedDelays - The list of selected delays.
     */
    handleSelectedDelaysChange(selectedDelays) {
        this.setState({ selectedDelays: selectedDelays });
    }

    /**
     * @description Resets the filters back to default.
     */
    handleResetFilters() {
        this.setState(
            {
                selectedDate: new Date().toISOString().split("T")[0],
                zoomLevel: 1,
                selectedDelays: [true, true, true, true, true, true, true],
                selectedCarriageways: HeatmapHelpers.getCarriagewaySelections(
                    this.state.routes
                )
            },
            this.handleSelectedRouteDataChange
        );
        Object.keys(this.resetMethods).forEach(methodName => {
            const method = this.resetMethods[methodName];

            method && method()
        });
    }

    /**
     * @description Checks whether the date has changed.
     * @returns {Boolean} Whether date has changed.
     */
    checkDateChange() {
        return (
            this.state.showLiveData &&
            HeatmapHelpers.getDateStringFromDate(new Date()) !==
            HeatmapHelpers.getDateStringFromDate(
                new Date(this.state.selectedDate)
            )
        );
    }

    // Notification related events

    /**
     * @description Adds a notification to the NotificationService.
     * @param {Object} notification - The notification details.
     * @returns {Object} The notification.
     */
    addNotification(notification) {
        return this.notificationService.addNotification(notification);
    }

    /**
     * @description Closes a notification via the NotificationService.
     * @param {number} id - The notification id.
     */
    closeNotification(id) {
        this.notificationService.removeNotification(id);
    }

    /**
     * @description Handles a click event of a notification action.
     * @param {number} id - The notification id.
     */
    handleNotificationActionClick(id) {
        this.notificationService.triggerNotificationAction(id);
    }

    /**
     * @description Handles a change to the notifications list.
     * @param {array} notifications - The list of notifications from the NotificationService.
     */
    handleNotificationsUpdate(notifications) {
        this.setState({
            notifications: notifications
        });
    }
}

App.defaultProps = {
    browserSupported: false,
    supportedBrowserVersions: {}
};

App.propTypes = {
    browserSupported: PropTypes.bool.isRequired,
    supportedBrowserVersions: PropTypes.object.isRequired
};

export default App;
